<template>
  <div class="profile-activity">
    <ProfilePageHeader title="Активность" />

    <!-- Ручной коэффициент (custom_activity_coefficient): анкета сохраняется,
         но на норму не влияет — человек должен это видеть здесь, а не гадать -->
    <p v-if="authStore.user?.custom_activity_coefficient" class="save-status" style="margin-bottom: 12px;">
      Коэффициент активности задан вручную: {{ authStore.user.custom_activity_coefficient }}. Анкета сохраняется, но на норму не влияет — вернуть расчёт можно в профиле, в карточке «Активность».
    </p>

    <!-- 1. Нетренировочная активность -->
    <div class="form-card">
      <label class="field-label">ОБЫЧНЫЙ ДЕНЬ — РАБОТА И БЫТ</label>
      <button
        v-for="opt in JOB_OPTIONS"
        :key="opt.id"
        class="option-card"
        :class="{ selected: job === opt.id }"
        @click="job = opt.id"
      >
        <div class="option-emoji">{{ opt.emoji }}</div>
        <div class="option-text">
          <div class="option-title">{{ opt.title }}</div>
          <div class="option-desc">{{ opt.desc }}</div>
        </div>
      </button>
      <p v-if="needsJobClarification" class="save-status" style="margin-top: 8px;">Раньше здесь был вариант «Активный». Уточните: на ногах или физический труд? От этого зависит норма.</p>
    </div>

    <!-- 2. Шаги -->
    <div class="form-card">
      <label class="field-label">СРЕДНЕЕ КОЛИЧЕСТВО ШАГОВ В ДЕНЬ</label>
      <input
        v-model="steps"
        type="number"
        inputmode="numeric"
        class="form-input"
        placeholder="например, 6000"
      />
    </div>

    <!-- 3. Тренировки -->
    <div class="form-card">
      <label class="field-label">ТРЕНИРОВКИ</label>

      <div v-for="act in ACTIVITY_TYPES" :key="act.key" class="workout-block" :ref="el => { if (el) workoutRefs[act.key] = el }">
        <!-- Кнопка выбора -->
        <button
          class="workout-chip"
          :class="{ selected: isSelected(act.key) }"
          @click="toggleActivity(act.key)"
        >
          <span class="workout-chip-label">{{ act.emoji }} {{ act.label }}</span>
          <span v-if="isSelected(act.key)" class="check">✓</span>
        </button>

        <!-- Текстовое поле для "Другое" -->
        <input
          v-if="act.key === 'other' && isSelected('other')"
          v-model="otherText"
          type="text"
          placeholder="Опишите активность..."
          class="form-input other-input"
          :class="{ 'other-input-error': validationErrors.other?.other }"
        />

        <!-- Частота и длительность -->
        <Transition name="expand">
          <div v-if="isSelected(act.key)" class="workout-details">
            <div class="detail-label" :class="{ 'detail-label-error': validationErrors[act.key]?.freq }">КАК ЧАСТО?</div>
            <div class="pill-row" :class="{ 'pill-row-error': validationErrors[act.key]?.freq }">
              <button
                v-for="f in FREQ_OPTIONS"
                :key="f.val"
                class="det-pill"
                :class="{ active: getFreq(act.key) === f.val }"
                @click="setFreq(act.key, f.val)"
              >{{ f.label }}</button>
            </div>
            <div class="detail-label" :class="{ 'detail-label-error': validationErrors[act.key]?.duration }">СРЕДНЯЯ ДЛИНА ОДНОГО ЗАНЯТИЯ?</div>
            <div class="pill-row" :class="{ 'pill-row-error': validationErrors[act.key]?.duration }">
              <button
                v-for="d in DUR_OPTIONS"
                :key="d.val"
                class="det-pill"
                :class="{ active: getDur(act.key) === d.val }"
                @click="setDur(act.key, d.val)"
              >{{ d.label }}</button>
            </div>
            <template v-if="act.key === 'yoga'">
              <div class="detail-label">КАКАЯ ЙОГА? <span style="font-weight: 400;">(необязательно)</span></div>
              <div class="pill-row">
                <button
                  v-for="s in YOGA_STYLES"
                  :key="s.id"
                  class="det-pill"
                  :class="{ active: getStyle(act.key) === s.id }"
                  @click="setStyle(act.key, s.id)"
                >{{ s.label }}</button>
              </div>
            </template>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Автосохранение статус -->
    <p v-if="saveStatus" class="save-status">{{ saveStatus }}</p>

    <!-- Назад в Профиль -->
    <button class="btn-primary" @click="$router.push('/profile')">
      Назад в Профиль
    </button>
  </div>
</template>

<script setup>
import ProfilePageHeader from '../components/ProfilePageHeader.vue'
import { JOB_OPTIONS, jobOf, WORK_TYPE_FROM_JOB, YOGA_STYLES } from '../utils/activityJobs.js'
import { ref, reactive, watch, onMounted, nextTick } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { useAuthStore } from '../store/auth.js'
import api from '../services/api.js'
import { track } from '../analytics.js'

const authStore = useAuthStore()

const ACTIVITY_TYPES = [
  { key: 'strength', emoji: '🏋️', label: 'Силовые тренировки' },
  { key: 'running', emoji: '🏃', label: 'Бег / интенсивное кардио' },
  { key: 'cycling', emoji: '🚴', label: 'Велоспорт / велотренажёр' },
  { key: 'crossfit', emoji: '🤸‍♀️', label: 'Функциональные / кроссфит / ВИИТ' },
  { key: 'team', emoji: '⚽️', label: 'Командные виды спорта' },
  { key: 'racket', emoji: '🎾', label: 'Ракеточные виды спорта' },
  { key: 'martial', emoji: '🥊', label: 'Единоборства' },
  { key: 'swimming', emoji: '🏊', label: 'Плавание' },
  { key: 'yoga', emoji: '🧘', label: 'Йога' },
  { key: 'dance', emoji: '💃', label: 'Танцы / групповые занятия' },
  { key: 'home', emoji: '📱', label: 'Домашние тренировки по приложению/видео' },
  { key: 'other', emoji: '🏅', label: 'Другое' },
]

const FREQ_OPTIONS = [
  { val: '1', label: '1 раз/нед' },
  { val: '2-3', label: '2-3 раза/нед' },
  { val: '4-5', label: '4-5 раз/нед' },
  { val: 'daily', label: 'Каждый день' },
]

const DUR_OPTIONS = [
  { val: '20', label: 'до 20 мин' },
  { val: '30', label: '20-30 мин' },
  { val: '45', label: '30-45 мин' },
  { val: '60', label: '~1 ч' },
  { val: '90', label: '~1.5 ч' },
  { val: '120', label: '~2 ч' },
  { val: '120+', label: '>2 ч' },
]

// Предзаполнение из профиля
const prev = authStore.user?.physical_activity || {}
const steps = ref(prev.steps ? String(prev.steps) : '')
const activities = reactive({}) // key → { freq, duration }
const otherText = ref('')
// Вид работы: новая анкета — как есть, старая — перевод из work_type
// (JOB_FROM_WORK_TYPE). Старый «Активный» мог быть физическим трудом —
// подсказываем уточнить.
const job = ref(jobOf(prev))
const needsJobClarification = !prev.job && prev.work_type === 'heavy'
const workoutRefs = reactive({})
const validationErrors = reactive({})

// Восстановить предыдущие ответы
if (prev.activities) {
  for (const a of prev.activities) {
    activities[a.type] = { freq: a.freq || null, duration: a.duration || null, style: a.style || null }
    if (a.type === 'other' && a.other_text) otherText.value = a.other_text
  }
}

function isSelected(key) { return !!activities[key] }
function toggleActivity(key) {
  if (activities[key]) {
    delete activities[key]
    delete validationErrors[key]
  } else {
    activities[key] = { freq: null, duration: null, style: null }
  }
}
function getStyle(key) { return activities[key]?.style }
function setStyle(key, val) { if (activities[key]) activities[key].style = val }
function getFreq(key) { return activities[key]?.freq }
function getDur(key) { return activities[key]?.duration }
function setFreq(key, val) {
  if (activities[key]) activities[key].freq = val
  if (validationErrors[key]?.freq) {
    validationErrors[key].freq = false
    if (!validationErrors[key].duration) delete validationErrors[key]
  }
}
function setDur(key, val) {
  if (activities[key]) activities[key].duration = val
  if (validationErrors[key]?.duration) {
    validationErrors[key].duration = false
    if (!validationErrors[key].freq) delete validationErrors[key]
  }
}

// Валидация: проверить что у всех выбранных активностей есть freq и duration,
// а у «Другое» заполнено текстовое поле
function validate() {
  let firstErrorKey = null
  for (const [key, data] of Object.entries(activities)) {
    const missingFreq = !data.freq
    const missingDur = !data.duration
    const missingOther = key === 'other' && !otherText.value.trim()
    if (missingFreq || missingDur || missingOther) {
      validationErrors[key] = { freq: missingFreq, duration: missingDur, other: missingOther }
      if (!firstErrorKey) firstErrorKey = key
    } else {
      delete validationErrors[key]
    }
  }
  if (firstErrorKey && workoutRefs[firstErrorKey]) {
    workoutRefs[firstErrorKey].scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
  return !firstErrorKey
}

// Собрать данные анкеты
function buildData() {
  const actList = []
  for (const [type, data] of Object.entries(activities)) {
    const item = { type, freq: data.freq, duration: data.duration }
    if (type === 'other' && otherText.value) item.other_text = otherText.value
    if (type === 'yoga' && data.style) item.style = data.style
    actList.push(item)
  }
  return {
    steps: parseInt(steps.value) || null,
    activities: actList,
    job: job.value,
    // work_type продолжаем писать: его читают старые сборки и серверная v1
    work_type: job.value ? WORK_TYPE_FROM_JOB[job.value] : null,
  }
}

// ── Автосохранение с debounce ──
const saveStatus = ref('')
let saveTimer = null
const isReady = ref(false)

onMounted(() => {
  window.scrollTo(0, 0)
  nextTick(() => { isReady.value = true })
})

function triggerAutoSave() {
  if (!isReady.value) return
  saveStatus.value = ''
  clearTimeout(saveTimer)
  saveTimer = setTimeout(async () => {
    // Валидация перед сохранением
    if (!validate()) {
      saveStatus.value = 'Заполните все поля для выбранных активностей'
      return
    }
    try {
      saveStatus.value = 'Сохранение...'
      const activityData = buildData()
      await authStore.updateProfile({ physical_activity: activityData })
      track('profile_activity_saved')
      saveStatus.value = 'Сохранено'
      setTimeout(() => { if (saveStatus.value === 'Сохранено') saveStatus.value = '' }, 2000)
    } catch {
      saveStatus.value = 'Ошибка сохранения'
    }
  }, 1000)
}

// Снимок данных при входе для сравнения при уходе
const initialDataHash = JSON.stringify(buildData())

// При уходе со страницы — запросить AI пересчёт PAL (если данные изменились)
onBeforeRouteLeave(() => {
  const currentHash = JSON.stringify(buildData())
  if (currentHash !== initialDataHash) {
    authStore.recalcPal(buildData())
  }
})

watch(steps, triggerAutoSave)
watch(job, triggerAutoSave)
watch(activities, triggerAutoSave, { deep: true })
watch(otherText, () => {
  // Clear validation error immediately when user types
  if (validationErrors.other?.other && otherText.value.trim()) {
    validationErrors.other.other = false
    if (!validationErrors.other.freq && !validationErrors.other.duration) delete validationErrors.other
  }
  triggerAutoSave()
})

</script>

<style scoped>
.profile-activity {
  min-height: 100vh;
  min-height: 100dvh;
  background: #FAF3E8;
  padding: 48px 16px 100px;
  padding-top: max(48px, calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 16px));
}

/* ── Form card ── */
.form-card {
  background: #FFFCF3;
  border-radius: 20px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 1px 4px rgba(65,41,26,0.06);
  margin-bottom: 16px;
}
.field-label {
  font-size: var(--fs-11);
  letter-spacing: 1.5px;
  font-weight: 700;
  color: var(--warm);
  text-transform: uppercase;
}

/* ── Option cards (work type) ── */
.option-card {
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 16px;
  border: 2px solid rgba(65,41,26,0.15);
  border-radius: 16px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}
.option-card.selected {
  border-color: var(--ink);
  background: #41291A;
  color: var(--cream);
}
.option-emoji { font-size: var(--fs-24); flex-shrink: 0; }
.option-text { flex: 1; min-width: 0; }
.option-title { font-weight: 700; font-size: var(--fs-16); color: inherit; }
.option-desc { font-size: var(--fs-13); color: inherit; opacity: 0.7; margin-top: 2px; line-height: 1.35; }
.option-card.selected .option-title,
.option-card.selected .option-desc { color: var(--cream); }

/* ── Form input ── */
.form-input {
  width: 100%;
  padding: 12px 14px;
  border: 2px solid rgba(65,41,26,0.15);
  border-radius: 12px;
  background: transparent;
  font-size: var(--fs-16);
  color: var(--ink);
  font-family: inherit;
  box-sizing: border-box;
}
.form-input:focus { outline: none; border-color: var(--ink); }
.other-input {
  margin-top: -6px;
  border-color: var(--ink);
  border-radius: 0;
  border-top: none;
}
.other-input-error {
  border-color: var(--error);
}

/* ── Workout chips (activity selection) ── */
.workout-block {
  display: flex;
  flex-direction: column;
}
.workout-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 2px solid rgba(65,41,26,0.15);
  border-radius: 14px;
  background: transparent;
  cursor: pointer;
  font-size: var(--fs-15);
  font-weight: 500;
  color: var(--ink);
  font-family: inherit;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}
.workout-chip.selected {
  border-color: var(--ink);
  background: #41291A;
  color: var(--cream);
  border-radius: 14px 14px 0 0;
}
.workout-chip-label { text-align: left; }
.check { font-weight: 700; }

/* ── Workout details (freq/dur) ── */
.workout-details {
  background: transparent;
  border: 2px solid #41291A;
  border-top: none;
  border-radius: 0 0 14px 14px;
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.detail-label {
  font-size: var(--fs-11);
  letter-spacing: 1px;
  font-weight: 700;
  color: var(--warm);
  margin-top: 4px;
  text-transform: uppercase;
  transition: color 0.2s;
}
.detail-label-error {
  color: var(--error);
}
.pill-row { display: flex; gap: 6px; flex-wrap: wrap; }
.pill-row-error .det-pill:not(.active) {
  border-color: var(--error);
}
.det-pill {
  flex: 0 1 auto;
  padding: 8px 12px;
  border: 2px solid rgba(65,41,26,0.15);
  border-radius: 10px;
  background: transparent;
  font-size: var(--fs-13);
  font-weight: 600;
  color: var(--ink);
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}
.det-pill.active {
  border-color: var(--ink);
  background: #41291A;
  color: var(--cream);
}

/* ── Expand transition ── */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.25s ease;
  overflow: hidden;
}
.expand-enter-from,
.expand-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* ── Save status ── */
.save-status {
  font-size: var(--fs-12);
  color: var(--ink-70);
  text-align: center;
  margin-bottom: 12px;
}

/* ── Primary button ── */

.btn-primary { margin-top: 4px; }
</style>
